import { createBrowserHistory } from "history";
import { Utilities } from "shared/utilities";
import { getRowsPerPage } from "processList/model/RowsPerPage";
import { GetProcessTemplatesRequest } from "./ProcessTemplate";
import { ProcessTemplateFilterType, pageSizeOptions } from "./List";

export const browserHistory = createBrowserHistory({
    basename: Utilities.getBaseUrlPath("")
});

export const processTemplateURLPattern = `/:base?/ProcessTemplates/:environmentName?/:agentGroupName?`;
export const processTemplateURLBase = `/:base?/ProcessTemplates`;

export enum ProcessTemplateListQueryParams {
    FilterCategory = 'filterCategory',
    FilterText = 'filterText',
    Page = 'page',
    PageSize = 'pageSize',
    SortBy = 'sortBy',
    Desc = 'desc',
    highlightTemplate = 'highLightTemplate'
}

export const getInitialPageNumber = (searchParams: URLSearchParams) => getPageNumber(searchParams) - 1;

export const getInitialPageSize = (searchParams: URLSearchParams) => {
    const optionIdx = pageSizeOptions.findIndex((o) => o.value === getPageSize(searchParams));
    return pageSizeOptions[(0 <= optionIdx ? optionIdx : 0)].value;
};

export const getInitialSorting = (searchParams: URLSearchParams) => ({ id: getSortBy(searchParams), desc: getDesc(searchParams) });

export const getInitialFilter = (searchParams: URLSearchParams) => {
    const filterCategory = getInitialFilterBy(searchParams);
    return { type: filterCategory as ProcessTemplateFilterType, values: getFilterValues(searchParams, filterCategory) };
};

export const pushSearchParamsToHistory = (pushedParams: URLSearchParams) => {
    const params = new URLSearchParams(location.search);
    params.delete(ProcessTemplateListQueryParams.highlightTemplate);
    pushedParams.forEach((value, key) => params.set(key, value));
    browserHistory.replace(window.location.pathname + "?" + params.toString());
};

export const getSearchParameters = (): GetProcessTemplatesRequest => {
    const search = new URLSearchParams(location.search);

    const filterBy = getFilterBy(search);
    const filterText = getFilterValues(search, filterBy as ProcessTemplateFilterType).join(',');
    const highLightTemplate = search.get(ProcessTemplateListQueryParams.highlightTemplate);

    return {
        pageNumber: getPageNumber(search),
        pageSize: getPageSize(search),
        sortBy: getSortBy(search),
        desc: getDesc(search),
        filterBy,
        filterText,
        ...(highLightTemplate ? { highLightTemplate } : {})
    };
};

const getPageNumber = (searchParams: URLSearchParams) => Number(searchParams.get(ProcessTemplateListQueryParams.Page) || 1);

// default to local storage (also used by process list), and fallback to 15
const getPageSize = (searchParams: URLSearchParams) => Number(searchParams.get(ProcessTemplateListQueryParams.PageSize) || getRowsPerPage() || 15);

const getSortBy = (searchParams: URLSearchParams) => searchParams.get(ProcessTemplateListQueryParams.SortBy) || 'name';

const getDesc = (searchParams: URLSearchParams) => searchParams.get(ProcessTemplateListQueryParams.Desc) === 'true';

const getFilterBy = (searchParams: URLSearchParams) => searchParams.get(ProcessTemplateListQueryParams.FilterCategory) || ProcessTemplateFilterType.Text;

const getInitialFilterBy = (searchParams: URLSearchParams) => {
    const filterCategoryFromQuery = getFilterBy(searchParams);
    const filterCategoryExists = Object.entries(ProcessTemplateFilterType).find((f) => String(f[1]) === filterCategoryFromQuery);
    const filterCategory = filterCategoryExists ? filterCategoryExists[1] : ProcessTemplateFilterType.Text;
    return filterCategory as ProcessTemplateFilterType;
};

const getFilterValues = (searchParams: URLSearchParams, type: ProcessTemplateFilterType) => {
    const filterText = searchParams.get(ProcessTemplateListQueryParams.FilterText)
    if (!filterText) {
        return [];
    }
    if (type === ProcessTemplateFilterType.Text) {
        return [filterText];
    }
    return filterText.split(',');
};